import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '@peca/commons';
import { ConfirmableActionService } from '@peca/backoffice/services/confirmable-action.service';
import { AsyncState } from '@peca/backoffice/models/async-state.model';
import { Pageable } from '@peca/backoffice/models/pageable.model';
import { AutocompleteItem, BreadcrumbItem, Pagination, PaginationInitialStateV2 } from '@peca/design-system';
import { BehaviorSubject } from 'rxjs';
import {
  MultiSelectAutocompleteItem,
  MultiSelectAutocompleteItemSource,
} from 'libs/design-system/src/lib/components/multi-select-autocomplete/multi-select-autocomplete.model';
import { ProductGroupDefaultService } from '../../../services/product-group-default.service';
import { ProductGroupDefaultDto, ProductGroupDefaultQuery } from '../../../models/product-group-default.model';

@Component({
  selector: 'peca-product-group-default-list',
  templateUrl: './product-group-default-list.component.html',
  styleUrls: ['./product-group-default-list.component.scss'],
  standalone: false,
})
export class ProductGroupDefaultListComponent implements OnInit {
  private readonly service = inject(ProductGroupDefaultService);
  private readonly toast = inject(ToastService);
  private readonly confirmable = inject(ConfirmableActionService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Operações', path: '/operacoes' },
    { label: 'Padrões de grupo de produtos' },
  ];

  state: AsyncState<ProductGroupDefaultDto[]> = { loading: true };
  readonly pagination$ = new BehaviorSubject<Pagination>(PaginationInitialStateV2);

  readonly searchForm = new FormGroup({
    productGroup: new FormControl<MultiSelectAutocompleteItem[]>([]),
  });

  productGroupSource = new MultiSelectAutocompleteItemSource();

  lastQuery: ProductGroupDefaultQuery = {
    page: PaginationInitialStateV2.page,
    size: PaginationInitialStateV2.size,
    sort: 'productGroupName,asc',
  };

  get items(): ProductGroupDefaultDto[] {
    return this.state.data ?? [];
  }

  ngOnInit(): void {
    this.fetch(this.lastQuery);

    // Garante que o autocomplete da busca mantenha apenas 1 item selecionado
    this.searchForm.get('productGroup')?.valueChanges.subscribe((items) => {
      if (items && items.length > 1) {
        const lastSelected = [items[items.length - 1]];
        this.searchForm.get('productGroup')?.setValue(lastSelected, { emitEvent: false });
      }
    });
  }

  fetch(query: ProductGroupDefaultQuery): void {
    this.lastQuery = query;
    this.state = { ...this.state, loading: true, failure: false };

    this.service.list(query).subscribe({
      next: (response) => {
        this.paginate(response);
        this.state = { data: response.items };
      },
      error: () => {
        this.state = { failure: true };
      },
    });
  }

  onProductGroupAutocomplete(search: string): void {
    if (search.trim().length < 3) {
      this.productGroupSource = new MultiSelectAutocompleteItemSource();
      return;
    }

    this.productGroupSource = new MultiSelectAutocompleteItemSource([], true);
    this.service.suggestProductGroups(search).subscribe({
      next: (items) => (this.productGroupSource = new MultiSelectAutocompleteItemSource(this.toMultiSelectItems(items))),
      error: () => (this.productGroupSource = new MultiSelectAutocompleteItemSource([], false, true)),
    });
  }

  onSubmitSearch(): void {
    const raw = this.searchForm.getRawValue();
    const productGroupId = raw.productGroup?.length ? raw.productGroup[0].id : undefined;

    this.fetch({
      ...this.lastQuery,
      page: 1,
      productGroupId,
    });
  }

  clearFilters(): void {
    this.searchForm.reset({ productGroup: [] });
    this.productGroupSource = new MultiSelectAutocompleteItemSource();

    this.fetch({
      ...this.lastQuery,
      page: 1,
      productGroupId: undefined,
    });
  }

  onPaginate(pagination: Pagination): void {
    const page = pagination.page <= 0 ? 1 : pagination.page;
    const query: ProductGroupDefaultQuery = {
      ...this.lastQuery,
      page,
      size: pagination.size,
    };
    this.fetch(query);
  }

  onClickCreate(): void {
    this.router.navigate(['novo'], { relativeTo: this.route });
  }

  onClickEdit(item: ProductGroupDefaultDto): void {
    this.router.navigate([item.id], { relativeTo: this.route });
  }

  onClickDelete(item: ProductGroupDefaultDto): void {
    this.confirmable
      .confirm(
        `Você está excluindo o padrão do grupo de produtos "${item.productGroupName}". Esta ação não pode ser desfeita.`,
        item.id,
        true
      )
      .subscribe(({ confirmed }) => {
        if (!confirmed) return;

        this.service.delete(item.id).subscribe({
          next: () => {
            this.toast.success('Padrão de grupo de produtos excluído com sucesso.');
            this.fetch(this.lastQuery);
          },
          error: () => {
            this.toast.failure('Não foi possível excluir o padrão de grupo de produtos.');
          },
        });
      });
  }

  private paginate(page: Pageable<ProductGroupDefaultDto>): void {
    const pagination = {
      page: page.currentPage,
      size: page.pageSize,
      total: page.totalPages,
      items: page.totalItems,
    };
    this.pagination$.next({ ...pagination, page: pagination.page });
  }

  private toMultiSelectItems(items: AutocompleteItem[]): MultiSelectAutocompleteItem[] {
    return items.map((item) => ({
      id: String(item.id),
      label: item.label,
      description: item.description,
      selected: false,
    }));
  }
}
